import { useEffect, useRef, useState } from "react";
import { isValidPin, KeyCopyError, unwrapKey, type KeyCopy } from "@timecapsule/crypto";
import { UNLOCK_CHOICES, UNLOCK_MINUTES } from "./keys";
import { isAborted, passkeyPrf } from "./passkey";
import { navigate } from "./router";
import { errorText, useT } from "./i18n";

/**
 * Asks for the key at the moment an action needs it (unlock on demand, 4b step 2g): PIN, then
 * the passkey; the key opens in this browser only. The action runs once and the key is dropped,
 * unless the member chooses to keep it unlocked for a few minutes.
 */
export function KeyDialog({
  purpose,
  copy,
  onDone,
  onCancel,
}: {
  /** Completes "Vrelis needs your key …", e.g. "to open this capsule" (t.keyDialog.purposes). */
  purpose: string;
  copy: KeyCopy;
  onDone: (key: Uint8Array, holdMinutes: number | null) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [pin, setPin] = useState("");
  const [keep, setKeep] = useState(false);
  const [minutes, setMinutes] = useState(UNLOCK_MINUTES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  function cancel() {
    abort.current?.abort();
    onCancel();
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
    };
    addEventListener("keydown", onKeyDown);
    return () => removeEventListener("keydown", onKeyDown);
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    abort.current = new AbortController();
    try {
      const prf = await passkeyPrf(copy.credentialId!, abort.current.signal);
      const key = await unwrapKey(copy, { prf, pin });
      onDone(key, keep ? minutes : null);
    } catch (err) {
      if (isAborted(err)) return;
      setError(err instanceof KeyCopyError ? t.keyBar.wrongPin : errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal card" role="dialog" aria-modal="true" aria-labelledby="key-dialog-title" onSubmit={submit}>
        <h3 id="key-dialog-title">{t.keyDialog.title}</h3>
        <p>{t.keyDialog.text(purpose)}</p>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          aria-label={t.common.pin}
          placeholder={t.common.pin}
          autoFocus
          value={pin}
          disabled={busy}
          onChange={(e) => setPin(e.target.value.trim())}
        />
        <label className="radio small">
          <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} disabled={busy} /> {t.keyDialog.keep}{" "}
          <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} disabled={busy || !keep} aria-label={t.keyDialog.minutes}>
            {UNLOCK_CHOICES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>{" "}
          {t.keyDialog.minutes}
        </label>
        <div className="modal-actions">
          <button className="primary" type="submit" disabled={!isValidPin(pin) || busy}>
            {busy ? t.common.waitingPasskey : t.common.continue}
          </button>
          <button type="button" onClick={cancel}>
            {t.common.cancel}
          </button>
        </div>
        {busy && <p className="small muted">{t.common.noWindowHint}</p>}
        {error && (
          <p className="error small">
            {error}{" "}
            <a
              href="/restore"
              onClick={(e) => {
                e.preventDefault();
                cancel();
                navigate("/restore");
              }}
            >
              {t.common.forgotPin}
            </a>
          </p>
        )}
      </form>
    </div>
  );
}
